import React from 'react'

import {
  Center,
  Container,
  Flex,
  GAccordion,
  GAccordionItem,
  Table,
  Tbody,
  Td,
  Tr,
} from '@gazin/ecommerce-ui'
import Link from 'next/link'

import dynamic from 'next/dynamic'

const ReactPlayer = dynamic(() => import('react-player'), {
  ssr: false,
})
import {
  ProductResponse,
  TechnicalSheetResponse,
} from 'src/services/marketplace-api/dtos'
import { Description, Information, Sheet } from './styles'
import { Dispatch, SetStateAction, useMemo } from 'react'
import { useWindowDimensions } from 'src/contexto/hooks/useWindowDimensions'
import { formatHtmlContent } from 'src/util/formatHtmlContent'

type ProductDetailsProps = {
  productContent: Partial<ProductResponse>
  technicalSheet: Array<TechnicalSheetResponse>
  showMore?: boolean
  accordionIndex: number
  setAccordionIndex: Dispatch<SetStateAction<number>>
  isCategoryPage?: boolean
}

export const ProductDetails = ({
  productContent,
  technicalSheet,
  showMore = true,
  accordionIndex,
  setAccordionIndex,
  isCategoryPage,
}: ProductDetailsProps) => {
  const { width } = useWindowDimensions()
  const isMobile = width! <= 768

  const semanticContent = formatHtmlContent(productContent?.descricao ?? '')

  const [semanticTitle, semanticDescription] = useMemo((): Array<
    string | undefined
  > => {
    if (!isCategoryPage) {
      return ['Informações do Produto', semanticContent]
    }

    const regexp = /^<h[1-6]>(.+?)<\/h[1-6]>/
    const [_, title, description] = semanticContent?.split(regexp) ?? []

    return [
      productContent?.seo_titulo ?? title,
      productContent?.seo_descricao ?? description,
    ]
  }, [isCategoryPage, semanticContent])

  return (
    <Container
      width="full"
      maxWidth="81.875rem"
      marginY={['1.25rem', '4rem']}
      p={isCategoryPage && !isMobile ? 0 : ''}
    >
      <GAccordion
        allowToggle
        index={[accordionIndex]}
        onChange={(e) => {
          setAccordionIndex(Number(e))
        }}
      >
        <Flex flexDir="column" gap={['0.0625rem', '1.5rem']}>
          {productContent?.descricao && productContent.descricao !== '' && (
            <Information id="product-information" $showMore={showMore}>
              <GAccordionItem
                title={`${semanticTitle}`}
                showMoreDetails={true}
                textButton="Ver mais detalhes"
                noOfLines={!showMore ? 10000 : isMobile ? 4 : 2}
              >
                <Description>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: `${semanticDescription}`,
                    }}
                  />
                </Description>

                {!!productContent.video && (
                  <Center>
                    <ReactPlayer
                      url={productContent.video}
                      light={false}
                      width={872}
                      height={488}
                    />
                  </Center>
                )}
              </GAccordionItem>
            </Information>
          )}

          {!!technicalSheet?.length && (
            <Sheet id="product-technical-sheet" w="full">
              <GAccordionItem title="Ficha Técnica">
                <Flex
                  className="item"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  mb="2.5rem"
                >
                  <Table textAlign="left" w="full">
                    <Tbody>
                      {technicalSheet?.map((item, key) => (
                        <Tr
                          key={key}
                          w="full"
                          display="grid"
                          gridTemplateColumns="1fr 2fr"
                          gap={{ base: '1rem', md: '2rem' }}
                          alignItems="center"
                          rounded="0.375rem"
                          minH="3rem"
                          m="0"
                          css={`
                            &:nth-child(odd) {
                              background: rgba(93, 168, 255, 0.08);
                            }
                          `}
                        >
                          <Td
                            display="flex"
                            border="none"
                            maxW="56.5rem"
                            fontSize="1rem"
                            fontWeight="600"
                            p={{
                              base: '.875rem 0rem .875rem 1rem',
                              md: '0.875rem 0rem 0.875rem 2rem',
                            }}
                            rounded="0.5rem 0rem 0rem 0.5rem"
                            lineHeight="1.125rem"
                            color="#6e6e6e"
                            w={{ base: '8.875rem', md: 'full' }}
                            css={`
                              @media (max-width: 20rem) {
                                width: 7.625rem;
                                font-size: 0.75rem;
                              }
                            `}
                          >
                            {item.campo}
                          </Td>
                          <Td
                            display="flex"
                            border="none"
                            maxW="56.5rem"
                            fontSize={{ base: '0.875rem', md: '1rem' }}
                            fontWeight="400"
                            p={{
                              base: '.875rem 1rem .875rem 0rem',
                              md: '0.875rem 2rem 0.875rem 0rem',
                            }}
                            rounded="0rem 0.5rem 0.5rem 0rem"
                            lineHeight="1.125rem"
                            color="#6e6e6e"
                            w={{ base: '8.875rem', md: 'full' }}
                            css={`
                              @media (max-width: 20rem) {
                                font-size: 0.75rem;
                              }
                            `}
                          >
                            {item.valor}
                          </Td>
                        </Tr>
                      ))}
                      {productContent?.manual && (
                        <Tr
                          w="full"
                          display="grid"
                          gridTemplateColumns="1fr 2fr"
                          gap={{ base: '1rem', md: '2rem' }}
                          alignItems="center"
                          rounded="0.375rem"
                          minH="3rem"
                          m="0"
                          css={`
                            &:nth-child(odd) {
                              background: rgba(93, 168, 255, 0.08);
                            }
                          `}
                        >
                          <Td
                            display="flex"
                            border="none"
                            maxW="56.5rem"
                            fontSize="1rem"
                            fontWeight="600"
                            p={{
                              base: '.875rem 0rem .875rem 1rem',
                              md: '0.875rem 0rem 0.875rem 2rem',
                            }}
                            rounded="0.5rem 0rem 0rem 0.5rem"
                            lineHeight="1.125rem"
                            color="#6e6e6e"
                            w={{ base: '8.875rem', md: 'full' }}
                            css={`
                              @media (max-width: 20rem) {
                                width: 7.625rem;
                                font-size: 0.75rem;
                              }
                            `}
                          >
                            Manual de instruções
                          </Td>
                          <Td
                            display="flex"
                            border="none"
                            maxW="56.5rem"
                            fontSize={{ base: '0.875rem', md: '1rem' }}
                            fontWeight="400"
                            p={{
                              base: '.875rem 1rem .875rem 0rem',
                              md: '0.875rem 2rem 0.875rem 0rem',
                            }}
                            rounded="0rem 0.5rem 0.5rem 0rem"
                            lineHeight="1.125rem"
                            color="#6e6e6e"
                            w={{ base: '8.875rem', md: 'full' }}
                            css={`
                              @media (max-width: 20rem) {
                                font-size: 0.75rem;
                              }
                            `}
                          >
                            <Link
                              href={productContent.manual.url}
                              target="_blank"
                            >
                              Download
                            </Link>
                          </Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </Flex>
              </GAccordionItem>
            </Sheet>
          )}
        </Flex>
      </GAccordion>
    </Container>
  )
}
